import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import type {
  ItemPreviaCnes,
  LocalAtendimento,
  PreviaCnes,
  ResultadoImportacaoCnes,
  SituacaoItemCnes,
} from '@/features/admin/tipos';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

/**
 * Teto de linhas desenhadas de uma vez.
 *
 * O quadro de um hospital universitário passa de 2.700 pessoas, e pintar tudo
 * de uma vez trava a janela. A seleção continua valendo sobre a busca inteira
 * — o corte é só do que aparece na tela.
 */
const MAXIMO_VISIVEL = 150;

const ROTULOS_SITUACAO: Record<SituacaoItemCnes, string> = {
  NOVO: 'Novo',
  ATUALIZAR: 'Atualizar',
  SEM_MUDANCA: 'Já cadastrado',
};

const TONS_SITUACAO: Record<SituacaoItemCnes, 'sucesso' | 'alerta' | 'neutro'> = {
  NOVO: 'sucesso',
  ATUALIZAR: 'alerta',
  SEM_MUDANCA: 'neutro',
};

const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

interface ImportarProfissionaisCnesProps {
  local: LocalAtendimento | null;
  aoFechar: () => void;
}

/**
 * Importação do quadro de profissionais de um estabelecimento, pela consulta
 * pública do CNES.
 *
 * A prévia existe porque o CNES devolve o quadro inteiro — recepção, limpeza e
 * apoio administrativo inclusive. Vêm marcadas apenas as ocupações que atendem
 * paciente; o resto fica à vista, desmarcado, para o administrador decidir.
 */
export function ImportarProfissionaisCnes({ local, aoFechar }: ImportarProfissionaisCnesProps) {
  const queryClient = useQueryClient();
  const [busca, setBusca] = React.useState('');
  const [escolhidos, setEscolhidos] = React.useState<Set<string>>(new Set());
  const [inativarAusentes, setInativarAusentes] = React.useState(true);
  const [precisaConfirmarEsvaziamento, setPrecisaConfirmarEsvaziamento] = React.useState(false);

  const aberto = local !== null;

  const previa = useQuery({
    queryKey: ['cnes-previa', local?.id],
    queryFn: () => api.get<PreviaCnes>(`/locais/${local!.id}/cnes/profissionais/previa`),
    enabled: aberto,
    // Consultar o DATASUS de novo a cada foco na janela seria abusar de um
    // serviço público de terceiros.
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: 0,
  });

  // Pré-marca os assistenciais assim que a prévia chega.
  React.useEffect(() => {
    if (!previa.data) return;
    setEscolhidos(
      new Set(
        previa.data.itens
          .filter((item) => item.assistencial && item.situacao !== 'SEM_MUDANCA')
          .map((item) => item.cns),
      ),
    );
  }, [previa.data]);

  React.useEffect(() => {
    if (!aberto) {
      setBusca('');
      setEscolhidos(new Set());
      setInativarAusentes(true);
      setPrecisaConfirmarEsvaziamento(false);
    }
  }, [aberto]);

  // Referência estável: o `?? []` criaria um array novo a cada render e
  // refaria o filtro de milhares de linhas sem necessidade.
  const itens = React.useMemo(() => previa.data?.itens ?? [], [previa.data]);

  const filtrados = React.useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return itens;
    return itens.filter(
      (item) =>
        normalizar(item.nome).includes(termo) ||
        normalizar(item.cboDescricao).includes(termo) ||
        item.cbo.includes(termo),
    );
  }, [itens, busca]);

  const alternar = (cns: string) => {
    setEscolhidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(cns)) proximo.delete(cns);
      else proximo.add(cns);
      return proximo;
    });
  };

  const marcarTodos = (alvos: ItemPreviaCnes[]) => {
    setEscolhidos((atual) => new Set([...atual, ...alvos.map((item) => item.cns)]));
  };

  const importar = useMutation({
    mutationFn: (confirmarEsvaziamento: boolean) =>
      api.post<ResultadoImportacaoCnes>(`/locais/${local!.id}/cnes/profissionais`, {
        cns: [...escolhidos],
        inativarAusentes,
        ...(confirmarEsvaziamento ? { confirmarEsvaziamento } : {}),
      }),
    onSuccess: async (resultado) => {
      toast.success(
        `${resultado.criados} cadastrado(s), ${resultado.atualizados} atualizado(s), ` +
          `${resultado.inativados} inativado(s).`,
      );
      await queryClient.invalidateQueries({ queryKey: ['profissionais'] });
      await queryClient.invalidateQueries({ queryKey: ['profissionais-ativos'] });
      aoFechar();
    },
    onError: (erro) => {
      // 409 é a trava contra esvaziar a lista: o CNES não devolveu ninguém.
      if (erro instanceof ErroApi && erro.status === 409) {
        setPrecisaConfirmarEsvaziamento(true);
        return;
      }
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível importar do CNES.',
      );
    },
  });

  if (!local) return null;

  const dados = previa.data;
  const ocultos = Math.max(0, filtrados.length - MAXIMO_VISIVEL);

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo className="max-w-3xl">
        <DialogoCabecalho>
          <DialogoTitulo>Importar profissionais do CNES</DialogoTitulo>
          <DialogoDescricao>
            Quadro do estabelecimento <strong>{local.cnes}</strong>, na consulta pública do DATASUS.
            Nada é gravado até você confirmar.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          {previa.isPending && (
            <div className="flex flex-col gap-2">
              <p className="text-silenciado-texto flex items-center gap-2 text-sm">
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Consultando o CNES…
              </p>
              <Esqueleto className="h-8 w-full" />
              <Esqueleto className="h-8 w-full" />
              <Esqueleto className="h-8 w-2/3" />
            </div>
          )}

          {previa.isError && (
            <div
              role="alert"
              className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo flex items-start gap-2 rounded-md border p-3 text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="break-words">
                  {previa.error instanceof ErroApi
                    ? previa.error.mensagemUsuario
                    : 'Não foi possível consultar o CNES.'}
                </p>
                <Botao
                  variante="contorno"
                  tamanho="sm"
                  className="mt-2"
                  onClick={() => void previa.refetch()}
                >
                  <RefreshCw aria-hidden="true" />
                  Tentar de novo
                </Botao>
              </div>
            </div>
          )}

          {dados && (
            <>
              {/* Confirmar o estabelecimento evita importar o quadro inteiro
                  de outra unidade por um dígito errado no cadastro. */}
              <div className="border-borda rounded-lg border p-3 text-sm">
                <p className="font-medium">
                  {dados.nomeFantasiaCnes ?? 'Estabelecimento sem nome'}
                </p>
                <p className="text-silenciado-texto text-xs">
                  CNES {dados.cnes}
                  {dados.competencia
                    ? ` · competência ${dados.competencia}`
                    : ' · competência vigente'}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Etiqueta tom="sucesso">{dados.resumo.novos} novos</Etiqueta>
                  <Etiqueta tom="alerta">{dados.resumo.atualizar} a atualizar</Etiqueta>
                  <Etiqueta>{dados.resumo.semMudanca} já cadastrados</Etiqueta>
                  {dados.resumo.ausentes > 0 && (
                    <Etiqueta tom="perigo">{dados.resumo.ausentes} ausentes do quadro</Etiqueta>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-48 flex-1">
                  <Search
                    className="text-silenciado-texto pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                    aria-hidden="true"
                  />
                  <Entrada
                    className="pl-9"
                    placeholder="Buscar por nome ou ocupação"
                    aria-label="Buscar no quadro do CNES"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                <Botao
                  variante="contorno"
                  tamanho="sm"
                  onClick={() => marcarTodos(filtrados.filter((item) => item.assistencial))}
                >
                  Marcar assistenciais
                </Botao>
                <Botao variante="contorno" tamanho="sm" onClick={() => marcarTodos(filtrados)}>
                  Marcar todos da busca
                </Botao>
                <Botao variante="fantasma" tamanho="sm" onClick={() => setEscolhidos(new Set())}>
                  Limpar
                </Botao>
              </div>

              <p className="text-silenciado-texto text-xs">
                {escolhidos.size} selecionado(s) de {itens.length} no quadro. Vêm marcadas apenas as
                ocupações que atendem paciente — o CNES devolve também recepção e apoio
                administrativo.
              </p>

              <ul className="border-borda max-h-80 overflow-y-auto rounded-lg border">
                {filtrados.length === 0 && (
                  <li className="text-silenciado-texto p-3 text-sm">
                    Ninguém no quadro corresponde à busca.
                  </li>
                )}

                {filtrados.slice(0, MAXIMO_VISIVEL).map((item) => (
                  <li key={item.cns} className="border-borda border-b last:border-b-0">
                    <label className="hover:bg-silenciado flex cursor-pointer items-start gap-3 p-2.5">
                      <input
                        type="checkbox"
                        className="accent-marca mt-1 size-4 shrink-0"
                        checked={escolhidos.has(item.cns)}
                        onChange={() => alternar(item.cns)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{item.nome}</span>
                          <Etiqueta tom={TONS_SITUACAO[item.situacao]}>
                            {ROTULOS_SITUACAO[item.situacao]}
                          </Etiqueta>
                          {!item.assistencial && <Etiqueta>não assistencial</Etiqueta>}
                        </span>
                        <span className="text-silenciado-texto block text-xs">
                          {item.cboDescricao} · CBO {item.cbo}
                          {item.outrosCbos.length > 0 && ` · também ${item.outrosCbos.join(', ')}`}
                          {item.vinculo ? ` · ${item.vinculo}` : ''}
                        </span>
                        {item.mudancas.length > 0 && (
                          <span className="text-silenciado-texto block text-xs italic">
                            {item.mudancas.join(' · ')}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}

                {ocultos > 0 && (
                  <li className="text-silenciado-texto bg-silenciado p-2.5 text-xs">
                    Mostrando {MAXIMO_VISIVEL} de {filtrados.length}. Refine a busca para ver o
                    resto — os botões de marcar valem sobre todos os {filtrados.length}.
                  </li>
                )}
              </ul>

              {dados.ausentes.length > 0 && (
                <div className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto rounded-md border p-3 text-sm">
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      className="accent-marca mt-1 size-4 shrink-0"
                      checked={inativarAusentes}
                      onChange={(e) => setInativarAusentes(e.target.checked)}
                    />
                    <span>
                      Inativar os {dados.ausentes.length} cadastrados que não vieram neste quadro.
                      Eles não são excluídos — agendamento antigo continua legível.
                      <span className="mt-1 block text-xs">
                        {dados.ausentes
                          .slice(0, 5)
                          .map((ausente) => ausente.nome)
                          .join(', ')}
                        {dados.ausentes.length > 5 && ` e mais ${dados.ausentes.length - 5}`}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {precisaConfirmarEsvaziamento && (
                <div
                  role="alert"
                  className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo rounded-md border p-3 text-sm"
                >
                  O CNES não devolveu nenhum profissional. Confirmar agora inativaria a lista
                  inteira — o que costuma ser competência sem dado ou instabilidade do DATASUS, e
                  não um estabelecimento sem corpo clínico.
                  <Botao
                    variante="destrutivo"
                    tamanho="sm"
                    className="mt-2"
                    onClick={() => importar.mutate(true)}
                    disabled={importar.isPending}
                  >
                    Inativar assim mesmo
                  </Botao>
                </div>
              )}
            </>
          )}
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => importar.mutate(false)}
            disabled={!dados || escolhidos.size === 0 || importar.isPending}
          >
            {importar.isPending ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : (
              <Download aria-hidden="true" />
            )}
            Importar {escolhidos.size > 0 ? `${escolhidos.size} ` : ''}profissionais
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}
